/**
 * Left-side tasks list panel for the single-order task screen.
 *
 * The list body uses `flex-1 min-h-0 basis-0` under a Tasks shell with a definite
 * viewport height so `overflow-y-auto` scrolls when rows exceed the column.
 * @module tasksListPanel
 */
import { ListItemCard } from "@risalabs_frontend_org/oasis-ui-kit";
import TasksListHeader from "./tasksListHeader";
import "./tasksListPanel.scss";

/**
 * Represents one task item shown in the left-side task list.
 */
export interface TaskListPanelItem {
  id: string;
  title: string;
  subtitle: string;
  metaLabel?: string;
  /** When true, the task is in progress (status only; no list indicator). */
  isInProgress?: boolean;
  /** When true, shows a green dot until the task is read/acknowledged. */
  isUpdated?: boolean;
}

/**
 * Props for {@link TasksListPanel}.
 */
interface TasksListPanelProps {
  tasks: TaskListPanelItem[];
  selectedTaskId?: string;
  isLoading?: boolean;
  onTaskSelect: (taskId: string) => void;
  onCreateTask: () => void;
  onLinkTask: () => void;
}

/**
 * Renders the reusable left section for task-list mode.
 *
 * @param props - Task rows and handlers for selection plus actions.
 * @returns Sidebar list panel with create/link controls and a scrollable task list body.
 */
const TasksListPanel = ({
  tasks,
  selectedTaskId,
  isLoading = false,
  onTaskSelect,
  onCreateTask,
  onLinkTask,
}: TasksListPanelProps) => {
  return (
    <aside className="flex min-h-0 w-[25rem] shrink-0 flex-col self-stretch border-r border-primaryGray-14">
      <div className="shrink-0">
        <TasksListHeader onCreateTask={onCreateTask} onLinkTask={onLinkTask} />
      </div>
      <div className="min-h-0 flex-1 basis-0 overflow-y-auto overflow-x-hidden bg-[#F7F9FA]">
        {isLoading ? (
          <p className="font-lato px-4 py-6 text-center text-sm text-primaryGray-3">
            Loading tasks…
          </p>
        ) : tasks.length === 0 ? (
          <p className="font-lato px-4 py-6 text-center text-sm text-primaryGray-3">
            No tasks for this case
          </p>
        ) : (
          <div className="flex flex-col">
            {tasks.map((task) => {
              const cardClassNames = [
                "tasks-list-panel__item-card",
                task.isUpdated && "tasks-list-panel__item-card--unread",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <ListItemCard
                  key={task.id}
                  title={task.title}
                  className={cardClassNames}
                  subtitle={task.subtitle}
                  trailingContent={
                    task.metaLabel ? (
                      <span className="font-lato text-[0.75rem] font-normal leading-[1.125rem] tracking-[0.0125rem] text-primaryGray-3">
                        {task.metaLabel}
                      </span>
                    ) : undefined
                  }
                  selected={task.id === selectedTaskId}
                  onClick={() => onTaskSelect(task.id)}
                  shouldTruncate={true}
                />
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
};

export default TasksListPanel;
